import { Button, Input } from '@artosera/ui';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import {
  usePlanComptable,
  useEcrirePlanComptable,
  type PlanComptable,
  type CompteComptable,
} from '../../api/comptabilite.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { ExportFec } from './ExportFec.js';
import styles from './AccesApiPage.module.css';
const COMPTES = [
  'clients',
  'ventes.normal',
  'ventes.reduit_5_5',
  'ventes.marge',
  'ventes.exonere',
  'tva.normal',
  'tva.reduit_5_5',
  'tva.marge',
  'droitDeSuite',
  'tresorerie.virement',
  'tresorerie.carte',
  'tresorerie.cheque',
  'tresorerie.especes',
  'tresorerie.autre',
] as const;
function compteDe(plan: PlanComptable, cle: (typeof COMPTES)[number]): CompteComptable {
  if (cle === 'clients' || cle === 'droitDeSuite') return plan[cle];
  const [groupe, regime] = cle.split('.') as ['ventes' | 'tva' | 'tresorerie', string];
  return (plan[groupe] as Record<string, CompteComptable>)[regime]!;
}
export function ComptabiliteSection(): JSX.Element | null {
  const { t } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const lecture = usePlanComptable(galerieSlug);
  if (lecture.isPending) return <p>{t('commun:chargement')}</p>;
  if (lecture.isError)
    return (
      <p role="alert">
        {estApiError(lecture.error) ? lecture.error.message : t('accesApi.erreurReseau')}
      </p>
    );
  if (!lecture.data.plan) return null;
  return (
    <section aria-labelledby="titre-comptabilite" className={styles.section} data-ui="accounting">
      <header className={styles.entete}>
        <h2 id="titre-comptabilite">{t('comptabilite.titre')}</h2>
        <ExportFec galerie={galerieSlug} />
      </header>
      <p>{t('fec.portee')}</p>
      <p>{t('comptabilite.validation')}</p>
      <FormulairePlan
        key={JSON.stringify(lecture.data.plan)}
        initial={lecture.data.plan}
        galerie={galerieSlug}
        modifiable={galerieALaction(galerie, 'settings:tax')}
      />
    </section>
  );
}
function FormulairePlan({
  initial,
  galerie,
  modifiable,
}: {
  initial: PlanComptable;
  galerie: string;
  modifiable: boolean;
}): JSX.Element {
  const { t } = useTranslation('regie');
  const [plan, setPlan] = useState(initial);
  const [message, setMessage] = useState<string | null>(null);
  const ecrire = useEcrirePlanComptable(galerie);
  const modifierCompte = (
    cle: (typeof COMPTES)[number],
    champ: 'numero' | 'libelle',
    valeur: string,
  ) => {
    const nouveau = structuredClone(plan);
    compteDe(nouveau, cle)[champ] = valeur;
    setPlan(nouveau);
  };
  return (
    <form
      className={styles.formulaire}
      onSubmit={(e) => {
        e.preventDefault();
        setMessage(null);
        void ecrire
          .mutateAsync(plan)
          .then(() => setMessage(t('comptabilite.enregistre')))
          .catch((err: unknown) =>
            setMessage(estApiError(err) ? err.message : t('accesApi.erreurReseau')),
          );
      }}
    >
      <div className={styles.plan}>
        {(['journalVentes', 'journalTresorerie'] as const).map((c) => (
          <div className={styles.compte} key={c}>
            <Field label={t(`comptabilite.plan.${c}`)} htmlFor={`plan-${c}`}>
              <Input
                id={`plan-${c}`}
                value={plan[c].code}
                onChange={(e) => setPlan((p) => ({ ...p, [c]: { ...p[c], code: e.target.value } }))}
                readOnly={!modifiable}
                pattern="[0-9A-Za-z]{1,8}"
                required
              />
            </Field>
            <Field
              label={t('comptabilite.libelle', { compte: t(`comptabilite.plan.${c}`) })}
              htmlFor={`lib-${c}`}
            >
              <Input
                id={`lib-${c}`}
                value={plan[c].libelle}
                onChange={(e) =>
                  setPlan((p) => ({ ...p, [c]: { ...p[c], libelle: e.target.value } }))
                }
                readOnly={!modifiable}
                maxLength={100}
                required
              />
            </Field>
          </div>
        ))}
        {COMPTES.map((c) => (
          <div className={styles.compte} key={c}>
            <Field label={t(`comptabilite.plan.${c}`)} htmlFor={`plan-${c}`}>
              <Input
                id={`plan-${c}`}
                value={compteDe(plan, c).numero}
                onChange={(e) => modifierCompte(c, 'numero', e.target.value)}
                readOnly={!modifiable}
                pattern="[0-9A-Za-z]{3,20}"
                inputMode="text"
                maxLength={20}
                required
              />
            </Field>
            <Field
              label={t('comptabilite.libelle', { compte: t(`comptabilite.plan.${c}`) })}
              htmlFor={`lib-${c}`}
            >
              <Input
                id={`lib-${c}`}
                value={compteDe(plan, c).libelle}
                onChange={(e) => modifierCompte(c, 'libelle', e.target.value)}
                readOnly={!modifiable}
                maxLength={100}
                required
              />
            </Field>
          </div>
        ))}
      </div>
      {modifiable && (
        <div>
          <Button type="submit" disabled={ecrire.isPending}>
            {t('comptabilite.enregistrer')}
          </Button>
        </div>
      )}
      {message && <p role={ecrire.isError ? 'alert' : 'status'}>{message}</p>}
    </form>
  );
}
